/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ButtonWidget", ["TextWidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Button widget.
   * @class ButtonWidget
   * @memberOf classes
   * @extends classes.TextWidgetBase
   * @publicdoc Widgets
   */
  cls.ButtonWidget = context.oo.Class(cls.TextWidgetBase, function ($super) {
    return /** @lends classes.ButtonWidget.prototype */ {
      __name: "ButtonWidget",

      /**
       * @type {classes.ImageWidget}
       */
      _image: null,

      /**
       * @type {HTMLElement}
       */
      _textElement: null,

      /**
       * @type {HTMLElement}
       */
      _mtButton: null,

      /**
       * Type of the button (normal, link or commandLink)
       * @type {null|string}
       */
      _buttonType: null,

      /** @type {boolean} */
      _autoScale: false,

      _scaleIconValue: null,

      /** @type {Object} */
      _alignment: null,

      /** @type {string|null} */
      _defaultColor: null,

      /** @function */
      _imageReadyHandler: null,
      /** @function */
      _afterLayoutHandler: null,

      /** @type {boolean} */
      _isTextAlign4STApplied: false,

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        this._layoutInformation = new cls.LayoutInformation(this);

        this._layoutInformation.getSizePolicyConfig().setAllShrinkable(false);
        this._layoutEngine = new cls.ButtonLayoutEngine(this);
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._mtButton = this._element.getElementsByClassName("mt-button")[0];
        this._textElement = this._mtButton.querySelector(".textimage span");
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._imageReadyHandler) {
          this._imageReadyHandler();
          this._imageReadyHandler = null;
        }
        if (this._afterLayoutHandler) {
          this._afterLayoutHandler();
          this._afterLayoutHandler = null;
        }
        if (this._image) {
          this._image.destroy();
          this._image = null;
        }
        this._textElement = null;
        this._mtButton = null;
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        // Disable click when not clicking on text nor image
        if (this._buttonType === "link" && domEvent.target.tagName.toLowerCase() !== "span" && domEvent.target.tagName.toLowerCase() !== "img") {
          return $super.manageMouseClick.call(this, domEvent);
        }

        if (this.isEnabled() || this.isInterruptable()) {
          this.emit(context.constants.widgetEvents.click, domEvent);
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this.isEnabled() && this._isActivationKey(keyString, repeat)) {
          this.emit(context.constants.widgetEvents.click, domKeyEvent);
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Sets the text of the button
       * @param {string} text text to display in the button
       * @publicdoc
       */
      setText: function (text) {
        this._labelText = text || null;
        this.domAttributesMutator(
          function () {
            this.toggleClass("hasText", text.length !== 0);
            this._textElement.textContent = text;
          }.bind(this)
        );
        this._refreshAccessibleName();

        if (this._layoutEngine) {
          if (this._layoutInformation && this._layoutInformation.getCurrentSizePolicy().isDynamic()) {
            this._layoutEngine.invalidateMeasure();
          }
        }

        // client QA code
        if (["qa_dialog_ready", "qa_menu_ready"].indexOf(text) >= 0) {
          gbc.QAService.bindQAReadyButton(this);
        }
      },

      /**
       * Gets the text of the button
       * @returns {string} the text displayed in the button
       * @publicdoc
       */
      getText: function () {
        return this._textElement.textContent;
      },

      /**
       * Sets the image of the button
       * @param {string} image the URL of the image to display
       * @publicdoc
       */
      setImage: function (image) {
        if (this._imageReadyHandler) {
          this._imageReadyHandler();
          this._imageReadyHandler = null;
        }
        if (image.length !== 0) {
          if (!this._image) {
            const opts = this.getBuildParameters();
            opts.inTable = false; // TODO image in button edit seem to not work correctly if inTable and buttonedit is intable
            this._image = cls.WidgetFactory.createWidget("Image", opts);
            this._image.setParentWidget(this);
            const imageContainer = this.getElement().getElementsByClassName("gbc_ButtonWidget__imageContainer")[0];
            imageContainer.appendChild(this._image.getElement());
            cls.DOMElementHelper.prependChild(this._mtButton.querySelector(".textimage"), imageContainer);
            this.setAutoScale(this._autoScale);
            if (this._defaultColor) {
              this._image.setDefaultColor(this._defaultColor);
            }
          }
          this._image.setSrc(image);
          this._imageReadyHandler = this._image.when(context.constants.widgetEvents.ready, this._imageLoaded.bind(this));
          this.addClasses("hasImage");

          if (!this._alignment && !this._isTextAlign4STApplied) {
            this.afterDomMutator(
              function () {
                // if no alignment set and no text
                if (this.getText().length <= 0) {
                  this.setStyle(".mt-button", {
                    "justify-content": "center"
                  });
                } else {
                  this.setStyle(".mt-button", {
                    "justify-content": "flex-start"
                  });
                }
              }.bind(this)
            );

            this.setStyle(".mt-button .textimage", {
              "align-self": "center",
              "align-items": "center"
            });
          }
        } else if (this._image) {
          this._image.destroy();
          this._image = null;
        }
      },

      /**
       * Callback once image has finish loading
       * @private
       */
      _imageLoaded: function (event, src) {
        this._layoutEngine.invalidateMeasure();
        this.emit(context.constants.widgetEvents.ready);
      },

      /**
       * Gets the image of the button
       * @returns {?string} the URL of the displayed image
       * @publicdoc
       */
      getImage: function () {
        if (this._image) {
          return this._image.getSrc();
        }
        return null;
      },

      /**
       * Define the button as autoscaled or not
       * @param {boolean} enabled the button autoscale mode
       * @publicdoc
       */
      setAutoScale: function (enabled) {
        this._autoScale = enabled;
        if (this._image) {
          this._image.setAutoScale(this._autoScale);
          cls.DOMElementHelper.toggleClass(this._image.getElement().parentElement, "gbc_autoScale", this._autoScale);
        }
      },

      /**
       * Set autoscale value as nnnpx
       * @param {string} value - css string value with valid units
       */
      setScaleIconValue: function (value) {
        this.scaleIconValue = value;
        if (this._image) {
          this._image.setScaleIconValue(value);
        }
      },

      /**
       * @inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        if (this.getParentWidget() && (this.getParentWidget().isDropDown || !this.getParentWidget().isVisible())) {
          const uiWidget = this.getUserInterfaceWidget();
          if (uiWidget) {
            uiWidget.domFocus(preventScroll);
          }
        } else {
          cls.DOMElementHelper.domFocus(this._mtButton, preventScroll);
        }
        $super.setFocus.call(this, fromMouse, preventScroll);
      },

      /**
       * The native `<button class="mt-button">` is the Tab stop for this
       * widget. It must reflect the enabled state — a disabled button
       * (e.g. an inactive action, or the inner button of a ButtonEditWidget
       * composite in DISPLAY) must leave the Tab order.
       * @inheritDoc
       */
      getDOMTabIndexElements: function () {
        return this._mtButton ? [this._mtButton] : [];
      },

      /** @inheritDoc */
      _ariaDisabledExposed: true,

      /**
       * The role-bearing element is the inner native <button>, not the root.
       * @inheritDoc
       */
      getAriaElement: function () {
        return this._mtButton;
      },

      /**
       * @inheritDoc
       */
      setEnabled: function (enabled) {
        $super.setEnabled.call(this, enabled);
        this.domAttributesMutator(
          function () {
            cls.DOMElementHelper.toggleClass(this._mtButton, "disabled", !enabled);
          }.bind(this)
        );
      },

      /**
       * @inheritDoc
       */
      setColor: function (color) {
        this.setStyle(".mt-button", {
          color: color ? color + " !important" : null
        });
      },

      /**
       * @inheritDoc
       */
      getColor: function () {
        return this.getStyle(".mt-button", "color");
      },

      /**
       * Set Default color (defined by DefaultTTF)
       * @param {string} color - rgb formatted or css name
       */
      setDefaultColor: function (color) {
        this._defaultColor = color;
        if (this._image) {
          this._image.setDefaultColor(color);
        }
      },

      /**
       * @inheritDoc
       */
      setBackgroundColor: function (color) {
        this.setStyle(".mt-button", {
          "background-color": color ? color + " !important" : null
        });
      },

      /**
       * @inheritDoc
       */
      getBackgroundColor: function () {
        return this.getStyle(".mt-button", "background-color");
      },

      /**
       * Align the content of the button
       * @param {string} align the button content alignment
       * @publicdoc
       */
      setContentAlign: function (align) {
        this.toggleClass("content-left", align === "left");
        this.toggleClass("content-right", align === "right");
        this.toggleClass("content-center", align === "center");
        this._isTextAlign4STApplied = true;
      },

      /**
       * @inheritDoc
       */
      setTitle: function (title) {
        $super.setTitle.call(this, title);
        if (this._image) {
          this._image.setTitle(title);
        }
      },

      /**
       * Hide the button text
       * @param {boolean} textHidden the button text visibility
       * @publicdoc
       */
      setTextHidden: function (textHidden) {
        this.toggleClass("text-hidden", textHidden);
      },

      /**
       * Define the button type (normal, link or commandLink)
       * @param {string} buttonType the button type
       * @publicdoc
       */
      setButtonType: function (buttonType) {
        if (this._buttonType) {
          this._mtButton.classList.remove("buttonType_" + this._buttonType);
          this.removeClass("buttonType_" + this._buttonType);
        }
        this._buttonType = buttonType;
        this._mtButton.classList.add("buttonType_" + buttonType);
        this.addClasses("buttonType_" + buttonType);

        // for buttons of type "link", add ARIA role to help screen readers to consider them as links
        cls.DOMElementHelper.updateAttribute(this._mtButton, "role", buttonType === "link" ? buttonType : null);
      },

      /**
       * Defines the alignment of the button text
       * @param {string} vertical - vertical position
       * @param {string} horizontal - horizontal position left, right or centered
       */
      setAlignment: function (vertical, horizontal) {
        this._alignment = {
          vertical: vertical,
          horizontal: horizontal
        };

        const _flex = {
          top: "flex-start",
          verticalCenter: "center",
          bottom: "flex-end",
          left: "flex-start",
          horizontalCenter: "center",
          right: "flex-end"
        };

        if (["left", "center", "right"].indexOf(horizontal) >= 0) {
          this.setStyle(".mt-button", {
            "justify-content": _flex[horizontal]
          });
        }

        if (["top", "verticalCenter", "bottom"].indexOf(vertical) >= 0) {
          this.setStyle(".mt-button .textimage", {
            "align-self": _flex[vertical]
          });
        }
      },
      /**
       * @inheritDoc
       */
      setInterruptable: function (interruptable) {
        $super.setInterruptable.call(this, interruptable);
        if (this._mtButton) {
          if (interruptable) {
            this._mtButton.setAttribute("interruptable", "interruptable");
          } else {
            this._mtButton.removeAttribute("interruptable");
          }
        }
      },
      /**
       * @inheritDoc
       */
      setInterruptableActive: function (isActive) {
        $super.setInterruptableActive.call(this, isActive);
        if (this._mtButton) {
          if (isActive) {
            this._mtButton.setAttribute("interruptable-active", "interruptable-active");
          } else {
            this._mtButton.removeAttribute("interruptable-active");
          }
        }
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Button", cls.ButtonWidget);
  cls.WidgetFactory.registerBuilder("Action", cls.ButtonWidget);
  cls.WidgetFactory.registerBuilder("MenuAction", cls.ButtonWidget);
});
